import { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Building2, TrendingUp, DollarSign, AlertTriangle,
  FileText, ChevronRight, Lock, ShieldCheck,
  BarChart3, Loader2,
} from 'lucide-react';

interface DealSummary {
  id: string;
  businessName: string;
  industry: string;
  city: string;
  country: string;
  askingPrice: number;
  adjustedEbitda: number;
  ebitdaMultiple: number;
  dscr: number;
  employeeCount: number;
  hasEnvironmentalCertification: boolean;
  hasLitigationHistory: boolean;
  customerConcentration: number;
  ownerDependencyRisk: string;
  impactDataSource: string;
  lenderOfferCount: number;
  ndaSigned: boolean;
}

interface Props {
  dealId: string;
  onProceed: () => void;
  onBack: () => void;
  language?: 'en' | 'fr';
}

const t = {
  en: {
    title: 'Deal Overview',
    subtitle: 'Review the deal summary before submitting your financing offer.',
    confidential: 'Confidential — NDA Required',
    ndaSigned: 'NDA Signed',
    section1: 'Business Summary',
    section2: 'Financial Metrics',
    section3: 'Risk Factors',
    section4: 'Impact Profile',
    askingPrice: 'Asking Price',
    ebitda: 'Adjusted EBITDA',
    multiple: 'Asking Multiple',
    dscr: 'DSCR (est.)',
    employees: 'Employees',
    industry: 'Industry',
    location: 'Location',
    customerConcentration: 'Customer Concentration',
    ownerDependency: 'Owner Dependency',
    litigation: 'Litigation History',
    envCert: 'Environmental Certification',
    impactSource: 'Data Source',
    selfDeclared: 'Self-declared by seller',
    offersSubmitted: 'Offers already submitted',
    dscrGood: 'Adequate (≥1.25x)',
    dscrWarning: 'Marginal (1.0–1.25x)',
    dscrBad: 'Insufficient (<1.0x)',
    proceed: 'Submit Financing Offer',
    back: 'Back to Dashboard',
    loading: 'Loading deal summary...',
    notFound: 'Deal not found or not available.',
    anonymous: 'Business details are anonymized until LOI execution.',
    lenderNotice: 'Your institution identity remains confidential until the buyer executes a Letter of Intent. Your offer will appear anonymously on the deal listing.',
  },
  fr: {
    title: 'Aperçu du Deal',
    subtitle: "Consultez le résumé du deal avant de soumettre votre offre de financement.",
    confidential: 'Confidentiel — NDA Requis',
    ndaSigned: 'NDA Signé',
    section1: "Résumé de l'entreprise",
    section2: 'Métriques financières',
    section3: 'Facteurs de risque',
    section4: "Profil d'impact",
    askingPrice: 'Prix demandé',
    ebitda: 'EBITDA Ajusté',
    multiple: 'Multiple demandé',
    dscr: 'DSCR (est.)',
    employees: 'Employés',
    industry: 'Secteur',
    location: 'Emplacement',
    customerConcentration: 'Concentration client',
    ownerDependency: 'Dépendance propriétaire',
    litigation: 'Historique litiges',
    envCert: 'Certification environnementale',
    impactSource: 'Source des données',
    selfDeclared: 'Auto-déclaré par le vendeur',
    offersSubmitted: 'Offres déjà soumises',
    dscrGood: 'Adéquat (≥1.25x)',
    dscrWarning: 'Marginal (1.0–1.25x)',
    dscrBad: 'Insuffisant (<1.0x)',
    proceed: 'Soumettre une offre de financement',
    back: 'Retour au tableau de bord',
    loading: 'Chargement du résumé...',
    notFound: 'Deal introuvable ou non disponible.',
    anonymous: "Les détails de l'entreprise sont anonymisés jusqu'à l'exécution du LOI.",
    lenderNotice: "L'identité de votre institution reste confidentielle jusqu'à ce que l'acheteur signe la Lettre d'Intention. Votre offre apparaîtra anonymement sur le listing.",
  },
};

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

function DscrBadge({ dscr, labels }: { dscr: number; labels: typeof t.en }) {
  if (dscr >= 1.25) {
    return <Badge className="bg-green-100 text-green-800">{dscr.toFixed(2)}x — {labels.dscrGood}</Badge>;
  }
  if (dscr >= 1.0) {
    return <Badge className="bg-yellow-100 text-yellow-800">{dscr.toFixed(2)}x — {labels.dscrWarning}</Badge>;
  }
  return <Badge className="bg-red-100 text-red-800">{dscr.toFixed(2)}x — {labels.dscrBad}</Badge>;
}

export function LenderDealPreScreen({ dealId, onProceed, onBack, language = 'en' }: Props) {
  const labels = t[language];
  const [deal, setDeal] = useState<DealSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [offerCount, setOfferCount] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const dealDoc = await getDoc(doc(db, 'deals', dealId));
        if (!dealDoc.exists()) {
          setLoading(false);
          return;
        }
        const data = dealDoc.data() as any;

        const faDoc = await getDoc(doc(db, 'financialAnalyses', dealId));
        const fa = faDoc.exists() ? (faDoc.data() as any) : {};

        const offersSnap = await getDocs(query(collection(db, 'lenderOffers')));
        const count = offersSnap.docs.filter((d) => d.data().dealId === dealId).length;
        setOfferCount(count);

        setDeal({
          id: dealId,
          businessName: data.businessName || '—',
          industry: data.industry || '—',
          city: data.city || '—',
          country: data.country || data.locationCountry || '—',
          askingPrice: data.askingPrice || 0,
          adjustedEbitda: fa.adjustedEbitda || data.ebitda || 0,
          ebitdaMultiple: fa.ebitdaMultiple || 0,
          dscr: fa.debtServiceCoverageRatio || 0,
          employeeCount: data.employeeCount || 0,
          hasEnvironmentalCertification: data.hasEnvironmentalCertification || false,
          hasLitigationHistory: data.hasLitigationHistory || false,
          customerConcentration: data.customerConcentration || 0,
          ownerDependencyRisk: fa.ownerDependencyRisk || data.ownerType || '—',
          impactDataSource: data.impactDataSource || 'self_declared',
          lenderOfferCount: count,
          ndaSigned: false,
        });
      } catch (e) {
        console.error('LenderDealPreScreen load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dealId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <p className="text-muted-foreground">{labels.loading}</p>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <AlertTriangle className="h-8 w-8 text-muted-foreground" />
        <p className="text-muted-foreground">{labels.notFound}</p>
        <Button variant="outline" onClick={onBack}>{labels.back}</Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{labels.title}</h1>
          <p className="text-muted-foreground mt-1">{labels.subtitle}</p>
        </div>
        <Badge variant="outline" className="gap-1 shrink-0">
          <Lock className="h-3 w-3" />
          {labels.confidential}
        </Badge>
      </div>

      {/* Lender notice */}
      <div className="flex items-start gap-3 rounded-lg border bg-muted/40 p-4">
        <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">{labels.lenderNotice}</p>
      </div>

      {/* Section 1 — Business Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5" />
            {labels.section1}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">{labels.industry}</p>
              <p className="font-medium">{deal.industry}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{labels.location}</p>
              <p className="font-medium">{deal.city}, {deal.country}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{labels.employees}</p>
              <p className="font-medium">{deal.employeeCount}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{labels.offersSubmitted}</p>
              <p className="font-medium">{offerCount}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground italic">{labels.anonymous}</p>
        </CardContent>
      </Card>

      {/* Section 2 — Financial Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <DollarSign className="h-5 w-5" />
            {labels.section2}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">{labels.askingPrice}</p>
              <p className="font-medium">{fmt(deal.askingPrice)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{labels.ebitda}</p>
              <p className="font-medium">{fmt(deal.adjustedEbitda)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{labels.multiple}</p>
              <p className="font-medium">{deal.ebitdaMultiple.toFixed(1)}x EBITDA</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{labels.dscr}</p>
              <DscrBadge dscr={deal.dscr} labels={labels} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3 — Risk Factors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="h-5 w-5" />
            {labels.section3}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between border-b pb-2">
            <span className="text-sm">{labels.customerConcentration}</span>
            <Badge
              className={
                deal.customerConcentration > 30
                  ? 'bg-red-100 text-red-800'
                  : deal.customerConcentration > 15
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-green-100 text-green-800'
              }
            >
              {deal.customerConcentration}%
            </Badge>
          </div>
          <div className="flex items-center justify-between border-b pb-2">
            <span className="text-sm">{labels.ownerDependency}</span>
            <Badge variant="outline">{deal.ownerDependencyRisk}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">{labels.litigation}</span>
            <Badge variant={deal.hasLitigationHistory ? 'destructive' : 'secondary'}>
              {deal.hasLitigationHistory ? '⚠ Yes' : '✓ None'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Section 4 — Impact Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5" />
            {labels.section4}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between border-b pb-2">
            <span className="text-sm">{labels.envCert}</span>
            <Badge variant={deal.hasEnvironmentalCertification ? 'default' : 'secondary'}>
              {deal.hasEnvironmentalCertification ? '✓ Yes' : 'No'}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">{labels.impactSource}</span>
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {labels.selfDeclared}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4">
        <Button variant="outline" onClick={onBack}>{labels.back}</Button>
        <Button onClick={onProceed} className="gap-2">
          <TrendingUp className="h-4 w-4" />
          {labels.proceed}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
